<script setup lang="ts">
import type { SelectOption } from "#imports";
interface ProductListingLimiterProps {
    currentLimit: number
    availableLimits?: number[]
    /**
     * Optional instance ID for multiple limiter instances
     */
    instanceId?: string
}

const props = withDefaults(defineProps<ProductListingLimiterProps>(), {
    availableLimits: () => [20, 40, 80, 120, 500],
    instanceId: ''
})

const emit = defineEmits<{
    (e: 'update:limit', limit: number): void
}>()

// Generate unique ID for the component
const uniqueId = useId()
const limiterId = computed(() => props.instanceId || `limiter-${uniqueId}`)

const model = ref(props.currentLimit)

watch(model, (newValue) => {
    if (newValue !== props.currentLimit) {
        emit('update:limit', Number(newValue))
    }
})

watch(() => props.currentLimit, (newValue) => {
    model.value = newValue
})

const { t } = useI18n()

const ALL_LIMIT = 500

const limitOptions = computed<SelectOption[]>(() => {
    const options = props.availableLimits.map(limit => ({
        value: limit,
        label: limit === ALL_LIMIT ? t('listing.limiter.all') : limit.toString()
    }))
    if (!props.availableLimits.includes(ALL_LIMIT)) {
        options.push({ value: ALL_LIMIT, label: t('listing.limiter.all') })
    }
    return options
})
</script>

<template>
    <div 
        :id="limiterId"
        class="product-listing-limiter flex flex-auto items-center justify-end gap-2 text-sm"
        :aria-label="$t('limiter.items_per_page')"
    >
        <div class="w-full relative peer">
            <FoundationSelect
                v-model="model"
                class="w-full min-w-25"
                :options="limitOptions"
                size="medium"
                :name="`${limiterId}-select`"
                :aria-label="$t('limiter.items_per_page')"
            />
        </div>
    </div>
</template>
